import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  User,
  Phone,
  Mail,
  Calendar,
  MapPin,
  FileText,
  Edit,
  AlertCircle,
  Clipboard,
  Stethoscope,
  FileCheck,
  Clock,
  Activity,
  Download,
  Eye,
  FileCheckIcon,
  Pill,
  CreditCard
} from 'lucide-react'
import { BlobProvider, PDFDownloadLink } from '@react-pdf/renderer'
import DischargeSummaryPDF from '../../components/DischargeSummaryPDF'
import OPDDischargePDF from '../../components/OPDDischargePDF'
import PatientBilling from '../../components/patients/PatientBilling'
import IPFinalBill from '../../components/patients/IPFinalBill'
import { getPatient } from '../../api/insuranceAPI'

const PatientView = () => {
  const { id: patientId } = useParams()
  const navigate = useNavigate()
  const [patient, setPatient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeSection, setActiveSection] = useState('personal')

  useEffect(() => {
    fetchPatient()
  }, [patientId])

  const fetchPatient = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getPatient(patientId)
      const data = response.data.data
      setPatient(data)
    } catch (err) {
      console.error('Failed to fetch patient:', err)
      const errorMessage = err.response?.data?.error?.message || err.response?.data?.message || 'Failed to load patient details'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch (error) {
      return dateString
    }
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return dateString
    }
  }

  const SectionButton = ({ id, icon: Icon, title, isActive }) => (
    <button
      onClick={() => setActiveSection(id)}
      className={`flex items-center px-4 py-3 rounded-lg transition-all ${
        isActive
          ? 'bg-blue-600 text-white shadow-md'
          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
      }`}
    >
      <Icon className="mr-2" size={18} />
      <span className="font-medium">{title}</span>
    </button>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-blue-600 mr-3" size={32} />
        <span className="text-gray-600">Loading patient details...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center">
        <AlertCircle className="text-red-600 mr-3" size={24} />
        <div>
          <h3 className="font-semibold text-red-800">Error</h3>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    )
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <User className="mx-auto text-gray-400 mb-4" size={48} />
        <h3 className="text-lg font-medium text-gray-800">Patient Not Found</h3>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/patients/list')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="text-gray-600" size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {patient.title ? `${patient.title} ${patient.full_name}` : patient.full_name}
            </h1>
            <p className="text-gray-600">UHID: {patient.uhid_no} | Patient ID: {patient.patient_id}</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/patients/${patientId}/edit`)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Edit size={18} className="mr-2" />
          Edit Patient
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <SectionButton
          id="personal"
          icon={User}
          title="Personal Info"
          isActive={activeSection === 'personal'}
        />
        <SectionButton
          id="medical"
          icon={Stethoscope}
          title="OPD Records"
          isActive={activeSection === 'medical'}
        />
        <SectionButton
          id="admissions"
          icon={Activity}
          title="Admissions"
          isActive={activeSection === 'admissions'}
        />
        <SectionButton
          id="billing"
          icon={CreditCard}
          title="Billing"
          isActive={activeSection === 'billing'}
        />
        <SectionButton
          id="ip-final-bill"
          icon={CreditCard}
          title="IP FINAL BILL"
          isActive={activeSection === 'ip-final-bill'}
        />
        <SectionButton
          id="documents"
          icon={FileText}
          title="Documents"
          isActive={activeSection === 'documents'}
        />
        <SectionButton
          id="discharge"
          icon={FileCheckIcon}
          title="Discharge"
          isActive={activeSection === 'discharge'}
        />
        <SectionButton
          id="timeline"
          icon={Clock}
          title="Timeline"
          isActive={activeSection === 'timeline'}
        />
      </div>

      {/* Content Sections */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {activeSection === 'personal' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <User className="mr-2 text-blue-600" size={24} />
              Personal Information
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Personal Information Card */}
              <div className="lg:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Full Name</label>
                      <p className="mt-1 text-lg font-medium text-gray-900">
                        {patient.title ? `${patient.title} ${patient.full_name}` : patient.full_name}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">UHID</label>
                      <p className="mt-1 text-lg font-medium text-gray-900">{patient.uhid_no}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">IP Number</label>
                      <p className="mt-1 text-lg text-gray-900">{patient.ip_no || 'N/A'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">Date of Birth</label>
                      <p className="mt-1 text-lg text-gray-900 flex items-center">
                        <Calendar size={18} className="mr-2 text-gray-400" />
                        {patient.dob ? formatDate(patient.dob) : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Gender</label>
                      <p className="mt-1 text-lg font-medium text-gray-900">{patient.gender || 'N/A'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">Age</label>
                      <p className="mt-1 text-lg font-medium text-gray-900">{patient.age ? `${patient.age} years` : 'N/A'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">Aadhar Number</label>
                      <p className="mt-1 text-lg text-gray-900">{patient.aadhar_no || 'N/A'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600">Center Code</label>
                      <div className="mt-1">
                        <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {patient.center_code}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information Card */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <Phone className="mr-2 text-blue-600" size={20} />
                  Contact Information
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Mobile</label>
                    <p className="mt-2 text-lg text-gray-900 flex items-center">
                      <Phone size={18} className="mr-2 text-gray-400" />
                      {patient.mobile || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className="mt-2 text-lg text-gray-900 flex items-center break-words">
                      <Mail size={18} className="mr-2 text-gray-400 flex-shrink-0" />
                      {patient.email || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">Address</label>
                    <p className="mt-2 text-sm text-gray-900">
                      {patient.address || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'medical' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <Stethoscope className="mr-2 text-blue-600" size={24} />
              OPD Records
            </h2>

            <div className="space-y-4">
              {patient.opd_consultation_records && patient.opd_consultation_records.length > 0 ? (
                patient.opd_consultation_records
                  .filter(record => {
                    // Filter out records without chief complaints
                    return record.chief_complaints && record.chief_complaints.trim() !== '';
                  })
                  .sort((a, b) => {
                    // Sort by created_at date, newest first
                    const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                    const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                    return dateB - dateA;
                  })
                  .map((record, index) => (
                  <div key={record.record_id || index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-gray-800">OPD Record #{record.record_id}</h3>
                        <p className="text-sm text-gray-600">{formatDateTime(record.created_at)}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* Chief Complaints */}
                      {record.chief_complaints && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Chief Complaints</label>
                          <p className="text-sm text-gray-700">{record.chief_complaints}</p>
                        </div>
                      )}

                      {/* History of Illness */}
                      {record.history_of_illness && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">History of Illness</label>
                          <p className="text-sm text-gray-700">{record.history_of_illness}</p>
                        </div>
                      )}

                      {/* Examination Findings */}
                      {record.examination && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Examination Findings</label>
                          <p className="text-sm text-gray-700">{record.examination}</p>
                        </div>
                      )}

                      {/* Provisional Diagnosis */}
                      {record.provisional_diagnosis && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Provisional Diagnosis</label>
                          <p className="text-sm text-gray-700">{record.provisional_diagnosis}</p>
                        </div>
                      )}

                      {/* Pain Scale */}
                      {record.pain_scale && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Pain Scale</label>
                          <p className="text-sm text-gray-700">{record.pain_scale}</p>
                        </div>
                      )}

                      {/* Investigations */}
                      {record.investigation && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Investigations</label>
                          <p className="text-sm text-gray-700">{record.investigation}</p>
                        </div>
                      )}

                      {/* Advice */}
                      {record.advice && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Medical Advice</label>
                          <p className="text-sm text-gray-700">{record.advice}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No OPD records found
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'admissions' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <Activity className="mr-2 text-blue-600" size={24} />
              Admission History
            </h2>

            <div className="space-y-4">
              {patient.admissions && patient.admissions.length > 0 ? (
                patient.admissions
                  .filter(admission => {
                    // Filter out incomplete records where all dates are null AND body_parts is null
                    const hasAnyDate = admission.date_of_admission || admission.date_of_procedure || admission.date_of_discharge;
                    const hasTreatment = admission.body_parts && String(admission.body_parts).trim() !== '' && String(admission.body_parts).toLowerCase() !== 'null';
                    return hasAnyDate || hasTreatment;
                  })
                  .map((admission, index) => (
                  <div key={admission.admission_id || index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-gray-800">Admission #{admission.admission_id}</h3>
                        <p className="text-sm text-gray-600">IP: {admission.ip_no || 'N/A'}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        admission.discharge_status === 'discharged'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {admission.discharge_status || 'Active'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div>
                        <label className="text-xs font-medium text-gray-600">Admission Date</label>
                        <p className="text-sm font-medium">{formatDate(admission.date_of_admission)}</p>
                        <p className="text-xs text-gray-500">{admission.time_of_admission}</p>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-gray-600">Procedure Date</label>
                        <p className="text-sm font-medium">{formatDate(admission.date_of_procedure)}</p>
                        <p className="text-xs text-gray-500">{admission.time_of_procedure}</p>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-gray-600">Discharge Date</label>
                        <p className="text-sm font-medium">{formatDate(admission.date_of_discharge)}</p>
                        <p className="text-xs text-gray-500">{admission.time_of_discharge}</p>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-gray-600">Surgeon</label>
                        <p className="text-sm font-medium">{admission.surgeon || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600">Pre-procedure Diagnosis</label>
                        <p className="text-sm text-gray-700">{admission.pre_procedure_diagnosis || 'N/A'}</p>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-gray-600">Procedure Details</label>
                        <p className="text-sm text-gray-700">{admission.detail_of_procedure || 'N/A'}</p>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-gray-600">Treatment Planned</label>
                        <p className="text-sm text-gray-700">{admission.treatment_planned || 'N/A'}</p>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-gray-600">Course of Stay</label>
                        <p className="text-sm text-gray-700">{admission.course_of_hospital_stay || admission.course_of_hospital_stay_text || 'N/A'}</p>
                      </div>

                      {admission.discharge_medicines && admission.discharge_medicines.length > 0 && (
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-2 block">Discharge Medicines</label>
                          <div className="space-y-2">
                            {admission.discharge_medicines.map((med, medIndex) => (
                              <div key={medIndex} className="bg-blue-50 rounded p-2 text-sm">
                                <div className="font-medium">{med.medicine_name}</div>
                                <div className="text-gray-600">
                                  {med.dose} - {med.frequency}
                                  {med.duration_days && ` for ${med.duration_days} days`}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {admission.discharge_instruction && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Discharge Instructions</label>
                          <p className="text-sm text-gray-700">{admission.discharge_instruction}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Activity className="mx-auto text-gray-400 mb-4" size={48} />
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No Admission Records</h3>
                  <p className="text-gray-600">This patient has no admission history in the insurance system.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'billing' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <CreditCard className="mr-2 text-blue-600" size={24} />
              Patient Billing
            </h2>
            <p className="text-gray-600 mb-6">
              View and manage all billing records, receipts, and financial transactions for this patient.
            </p>
            <PatientBilling patientId={patient?.patient_id} />
          </div>
        )}

        {activeSection === 'ip-final-bill' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <CreditCard className="mr-2 text-blue-600" size={24} />
              IP FINAL BILL
            </h2>
            <IPFinalBill patient={patient} />
          </div>
        )}

        {activeSection === 'discharge' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <FileCheckIcon className="mr-2 text-blue-600" size={24} />
              Discharge Summaries
            </h2>

            {!patient.admissions || patient.admissions.length === 0 ? (
              <div className="text-center py-8">
                <FileCheckIcon className="mx-auto text-gray-400 mb-4" size={48} />
                <h3 className="text-lg font-medium text-gray-800 mb-2">No Discharge Records</h3>
                <p className="text-gray-600">This patient has no discharge records in the insurance system.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* IP Discharge Summaries */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Inpatient (IP) Discharges</h3>
                  {patient.admissions
                    .filter(adm => {
                      // Filter out incomplete records
                      const hasAnyDate = adm.date_of_admission || adm.date_of_procedure || adm.date_of_discharge;
                      const hasTreatment = adm.body_parts && String(adm.body_parts).trim() !== '' && String(adm.body_parts).toLowerCase() !== 'null';
                      return hasAnyDate || hasTreatment;
                    })
                    .some(adm => adm.date_of_discharge) ? (
                    <div className="space-y-3">
                      {patient.admissions
                        .filter(adm => {
                          // Filter out incomplete records
                          const hasAnyDate = adm.date_of_admission || adm.date_of_procedure || adm.date_of_discharge;
                          const hasTreatment = adm.body_parts && String(adm.body_parts).trim() !== '' && String(adm.body_parts).toLowerCase() !== 'null';
                          return (hasAnyDate || hasTreatment) && adm.date_of_discharge && String(adm.date_of_discharge).trim() !== "";
                        })
                        .map((admission, index) => {
                          // Backend already provides discharge_medicines for each admission
                          const dischargeMedicines = admission.discharge_medicines || [];

                          const admissionPayload = {
                            admission: {
                              ...admission,
                              discharge_medicines: dischargeMedicines,
                              course_of_hospital_stay: admission.course_of_hospital_stay || 
                                `${Math.max(1, Math.ceil((new Date(admission.date_of_discharge) - new Date(admission.date_of_admission)) / (1000 * 60 * 60 * 24)) + 1)} days`
                            }
                          };

                          return (
                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-800">Admission ID: {admission.admission_id}</h4>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {admission.detail_of_procedure || admission.treatment_planned || 'Inpatient Admission'}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Admitted: {formatDate(admission.date_of_admission)} | 
                                    Discharged: {formatDate(admission.date_of_discharge)}
                                  </p>
                                  {admission.discharge_instruction && (
                                    <p className="text-sm text-gray-700 mt-2 italic border-l-4 border-blue-500 pl-2">
                                      {admission.discharge_instruction}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <BlobProvider document={<DischargeSummaryPDF patient={patient} admission={admissionPayload} />}>
                                  {({ url, loading }) => (
                                    <a
                                      href={url || "#"}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`px-3 py-2 rounded text-sm flex items-center ${
                                        loading
                                          ? "bg-gray-400 cursor-not-allowed text-white"
                                          : "bg-blue-600 text-white hover:bg-blue-700"
                                      }`}
                                      onClick={loading ? (e) => e.preventDefault() : null}
                                    >
                                      <Eye size={14} className="mr-1" />
                                      {loading ? 'Generating...' : 'View PDF'}
                                    </a>
                                  )}
                                </BlobProvider>
                                <PDFDownloadLink
                                  document={<DischargeSummaryPDF patient={patient} admission={admissionPayload} />}
                                  fileName={`${patient.full_name}_IP_Discharge_${admission.admission_id || ''}.pdf`}
                                  className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center"
                                >
                                  {({ loading }) => (
                                    <>
                                      <Download size={14} className="mr-1" />
                                      {loading ? 'Preparing...' : 'Download'}
                                    </>
                                  )}
                                </PDFDownloadLink>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No inpatient discharge records found.</p>
                    </div>
                  )}
                </div>

                {/* OPD Discharge Summaries */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Outpatient (OPD) Discharges</h3>
                  {(() => {
                    // Debug logging
                    console.log('DEBUG OPD Data:', {
                      hasOpdRecords: !!patient.opd_consultation_records,
                      opdRecordsCount: patient.opd_consultation_records?.length || 0,
                      opdRecords: patient.opd_consultation_records,
                      hasPrescriptions: !!patient.prescriptions,
                      prescriptionsCount: patient.prescriptions?.length || 0,
                      prescriptions: patient.prescriptions
                    });
                    return null;
                  })()}
                  {patient.opd_consultation_records && patient.opd_consultation_records.length > 0 ? (
                    <div className="space-y-3">
                      {patient.opd_consultation_records
                        .filter(consultation => {
                          // Only show consultations that have prescriptions
                          if (!consultation.chief_complaints || consultation.chief_complaints.trim() === '') {
                            console.log('Filtered out - no chief complaints:', consultation.record_id);
                            return false;
                          }
                          if (!consultation.record_id) {
                            console.log('Filtered out - no record_id');
                            return false;
                          }
                          if (!consultation.created_at) {
                            console.log('Filtered out - no created_at:', consultation.record_id);
                            return false;
                          }
                          
                          // Flatten prescriptions array - each group is an array of prescriptions with same timestamp
                          const allPrescriptions = (patient.prescriptions || []).flat();
                          
                          // Match prescriptions by timestamp (created_at)
                          const consultationTime = consultation.created_at;
                          const linkedPrescriptions = allPrescriptions.filter(p => 
                            p.created_at === consultationTime && (!p.admission_id || p.admission_id === null)
                          );
                          
                          console.log('Consultation matching:', {
                            record_id: consultation.record_id,
                            consultationTime,
                            linkedPrescriptionsCount: linkedPrescriptions.length,
                            linkedPrescriptions
                          });
                          
                          if (linkedPrescriptions.length === 0) {
                            console.log('Filtered out - no linked prescriptions:', consultation.record_id);
                            return false;
                          }
                          
                          // Exclude if all linked prescriptions are discharge prescriptions
                          const hasNonDischarge = linkedPrescriptions.some(
                            p => (p.prescription_text || '').trim() !== 'Discharge Prescription'
                          );
                          
                          if (!hasNonDischarge) {
                            console.log('Filtered out - all discharge prescriptions:', consultation.record_id);
                          }
                          
                          return hasNonDischarge;
                        })
                        .map((consultation, index) => {
                        // Get prescriptions for this consultation by matching timestamp
                        const allPrescriptions = (patient.prescriptions || []).flat();
                        const consultationTime = consultation.created_at;
                        const opdMedicines = allPrescriptions.filter(p =>
                          p.created_at === consultationTime && (!p.admission_id || p.admission_id === null)
                        );

                        console.log('Rendering OPD card:', {
                          consultationId: consultation.record_id,
                          consultationTime,
                          opdMedicinesCount: opdMedicines.length,
                          opdMedicines,
                          patient: {
                            full_name: patient.full_name,
                            age: patient.age,
                            gender: patient.gender,
                            mobile: patient.mobile,
                            uhid_no: patient.uhid_no
                          },
                          consultation: {
                            record_id: consultation.record_id,
                            chief_complaints: consultation.chief_complaints,
                            created_at: consultation.created_at
                          }
                        });

                        return (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-800">OPD Consultation - {formatDate(consultation.created_at)}</h4>
                                <p className="text-sm text-gray-600 mt-1">
                                  {(consultation.chief_complaints || '').substring(0, 100)}
                                  {(consultation.chief_complaints || '').length > 100 ? '...' : ''}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Diagnosis: {consultation.provisional_diagnosis || 'N/A'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <BlobProvider document={<OPDDischargePDF patient={patient} consultation={consultation} prescriptions={opdMedicines} />}>
                                {({ url, loading, error }) => {
                                  console.log('BlobProvider state:', { url, loading, error, hasConsultation: !!consultation, hasPrescriptions: opdMedicines.length });
                                  if (error) {
                                    console.error('PDF generation error:', error);
                                  }
                                  return (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (url && !loading) {
                                          window.open(url, '_blank', 'noopener,noreferrer');
                                        }
                                      }}
                                      disabled={loading || !url}
                                      className={`px-3 py-2 rounded text-sm flex items-center ${
                                        loading || !url
                                          ? "bg-gray-400 cursor-not-allowed text-white"
                                          : "bg-blue-600 text-white hover:bg-blue-700"
                                      }`}
                                    >
                                      <Eye size={14} className="mr-1" />
                                      {loading ? 'Generating...' : error ? 'Error' : 'View PDF'}
                                    </button>
                                  );
                                }}
                              </BlobProvider>
                              <PDFDownloadLink
                                document={<OPDDischargePDF patient={patient} consultation={consultation} prescriptions={opdMedicines} />}
                                fileName={`${patient.title ? `${patient.title}_${patient.full_name}` : patient.full_name}_OPD_Discharge_${consultation.record_id || ''}.pdf`}
                                className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center"
                              >
                                {({ loading }) => (
                                  <>
                                    <Download size={14} className="mr-1" />
                                    {loading ? 'Preparing...' : 'Download'}
                                  </>
                                )}
                              </PDFDownloadLink>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No outpatient discharge records found.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeSection === 'timeline' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <Clock className="mr-2 text-blue-600" size={24} />
              Medical Timeline
            </h2>

            <div className="space-y-4">
              {/* System Events */}
              <div className="border-l-4 border-blue-500 pl-4 pb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <User size={14} className="text-white" />
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-800">Patient Registered</p>
                    <p className="text-xs text-gray-600">{formatDateTime(patient.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Admission Events */}
              {patient.admissions && patient.admissions
                .filter(admission => {
                  // Filter out incomplete records
                  const hasAnyDate = admission.date_of_admission || admission.date_of_procedure || admission.date_of_discharge;
                  const hasTreatment = admission.body_parts && String(admission.body_parts).trim() !== '' && String(admission.body_parts).toLowerCase() !== 'null';
                  return hasAnyDate || hasTreatment;
                })
                .map((admission, index) => (
                <div key={index} className="border-l-4 border-green-500 pl-4 pb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <Activity size={14} className="text-white" />
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-gray-800">
                        Admission #{admission.admission_id}
                      </p>
                      <p className="text-xs text-gray-600">
                        Admitted: {formatDateTime(admission.date_of_admission)}
                      </p>
                      {admission.date_of_discharge && (
                        <p className="text-xs text-gray-600">
                          Discharged: {formatDateTime(admission.date_of_discharge)}
                        </p>
                      )}
                      <p className="text-xs text-gray-700 mt-1">
                        Procedure: {admission.detail_of_procedure || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Prescription Events */}
              {patient.prescriptions && patient.prescriptions.flat().map((prescription, index) => (
                <div key={index} className="border-l-4 border-purple-500 pl-4 pb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                      <Pill size={14} className="text-white" />
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-gray-800">
                        Prescription: {prescription.medicine_name}
                      </p>
                      <p className="text-xs text-gray-600">
                        {formatDateTime(prescription.created_at)}
                      </p>
                      <p className="text-xs text-gray-700 mt-1">
                        {prescription.dose} - {prescription.frequency}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Last Updated */}
              <div className="border-l-4 border-gray-500 pl-4 pb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
                    <Clock size={14} className="text-white" />
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-800">Last Updated</p>
                    <p className="text-xs text-gray-600">{formatDateTime(patient.updated_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'documents' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <FileText className="mr-2 text-blue-600" size={24} />
              Documents
            </h2>

            {patient.documents ? (
              <div className="space-y-8">
                {/* Aadhar Card Section */}
                {patient.documents.aadhar && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-4">Aadhar Card</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-600 mb-2">Aadhar Number</p>
                        <p className="text-gray-900 font-medium">
                          {patient.documents.aadhar.number || patient.aadhar_no || 'N/A'}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-600 mb-2">Front Image</p>
                        {patient.documents.aadhar.front_image ? (
                          <a
                            href={patient.documents.aadhar.front_image}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                          >
                            <Eye size={16} className="mr-1" />
                            View Front
                          </a>
                        ) : (
                          <p className="text-gray-500">Not available</p>
                        )}
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-600 mb-2">Back Image</p>
                        {patient.documents.aadhar.back_image ? (
                          <a
                            href={patient.documents.aadhar.back_image}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                          >
                            <Eye size={16} className="mr-1" />
                            View Back
                          </a>
                        ) : (
                          <p className="text-gray-500">Not available</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Signatures Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-4">Signatures</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-600 mb-2">Patient Signature</p>
                      {patient.documents.patient_signature ? (
                        <a
                          href={patient.documents.patient_signature}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                        >
                          <Eye size={16} className="mr-1" />
                          View Signature
                        </a>
                      ) : (
                        <p className="text-gray-500">Not available</p>
                      )}
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-600 mb-2">Relative Signature</p>
                      {patient.documents.relative_signature ? (
                        <a
                          href={patient.documents.relative_signature}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                        >
                          <Eye size={16} className="mr-1" />
                          View Signature
                        </a>
                      ) : (
                        <p className="text-gray-500">Not available</p>
                      )}
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-600 mb-2">Staff Signature</p>
                      {patient.documents.staff_signature ? (
                        <a
                          href={patient.documents.staff_signature}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                        >
                          <Eye size={16} className="mr-1" />
                          View Signature
                        </a>
                      ) : (
                        <p className="text-gray-500">Not available</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Reports Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-4">Medical Reports</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-600 mb-3">Current Reports</p>
                      {patient.documents.current_reports && patient.documents.current_reports.length > 0 ? (
                        <div className="space-y-2">
                          {patient.documents.current_reports.map((report, index) => (
                            <a
                              key={index}
                              href={report}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                            >
                              <Download size={14} className="mr-1" />
                              Report {index + 1}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">No current reports</p>
                      )}
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-600 mb-3">Old Reports</p>
                      {patient.documents.old_reports && patient.documents.old_reports.length > 0 ? (
                        <div className="space-y-2">
                          {patient.documents.old_reports.map((report, index) => (
                            <a
                              key={index}
                              href={report}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                            >
                              <Download size={14} className="mr-1" />
                              Report {index + 1}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">No old reports</p>
                      )}
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-600 mb-3">Latest Reports</p>
                      {patient.documents.latest_reports && patient.documents.latest_reports.length > 0 ? (
                        <div className="space-y-2">
                          {patient.documents.latest_reports.map((report, index) => (
                            <a
                              key={index}
                              href={report}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                            >
                              <Download size={14} className="mr-1" />
                              Report {index + 1}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">No latest reports</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Disease Forms Section */}
                {patient.documents.disease_forms && patient.documents.disease_forms.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-4">Disease Forms</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {patient.documents.disease_forms.map((form, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="text-sm font-medium text-gray-600 mb-1">Form Type</p>
                              <p className="text-gray-900 font-medium">
                                {form.diagnosis || `Disease Form ${index + 1}`}
                              </p>
                            </div>
                            {form.upload_date && (
                              <span className="text-xs text-gray-500">
                                {formatDate(form.upload_date)}
                              </span>
                            )}
                          </div>
                          <a
                            href={form.disease_form}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            <Eye size={14} className="mr-1" />
                            View Form
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText size={48} className="mx-auto mb-4 text-gray-400" />
                <p>No documents available</p>
                <p className="text-sm mt-2">Documents will appear here once synced from PMS</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4 justify-end">
        <button
          onClick={() => navigate('/patients/list')}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Back to List
        </button>
        <button
          onClick={() => navigate(`/patients/${patientId}/edit`)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <Edit size={18} className="mr-2" />
          Edit Patient
        </button>
      </div>
    </div>
  )
}

export default PatientView
